import type { DirectusCompany } from '@/types/directus-types'
import generateUniqueLocalID from '../utils/generateUniqueLocalID'
import getDirectusImgUrl from '../utils/getDirectusImgUrl'

export default class Fleet {
  // -------------------------------------------------------------------- //
  // ------------------------- Global class state ----------------------- //
  // -------------------------------------------------------------------- //

  // global record of class instance ids this session.
  static usedIds: string[] = []

  // -------------------------------------------------------------------- //
  // ------------------------------- State ------------------------------ //
  // -------------------------------------------------------------------- //

  /** local unique id. */
  localId: string

  /** Flag that represents if a driver is allowed to manually match or change vehicle data. */
  driverEditAccess: boolean

  /** Display name for this fleet. */
  name?: string

  /** Fleets's logo asset file UUID. */
  logo?: string

  /** Flag that represents if a drivers are allowed to book vehicles for this fleet. */
  allowVehicleBooking: boolean

  /** Flag that represents if a drivers are allowed to book private chargers for this fleet. */
  allowPrivateChargerBooking: boolean

  /** Flag that represents if vehicle bookings for this fleet are handled by an external service. */
  thirdPartyVehicleBookingOnly: boolean

  /** URL for external vehicle booking service. */
  thirdPartyVehicleBookingUrl?: string

  // -------------------------------------------------------------------- //
  // --------------------------- Constructor ---------------------------- //
  // -------------------------------------------------------------------- //

  constructor({
    localId = undefined,
    driverEditAccess = false,
    name = undefined,
    logo = undefined,
    allowVehicleBooking = false,
    allowPrivateChargerBooking = false,
    thirdPartyVehicleBookingOnly = false,
    thirdPartyVehicleBookingUrl = undefined,
  }: {
    localId?: string
    driverEditAccess?: boolean
    name?: string
    logo?: string
    allowVehicleBooking?: boolean
    allowPrivateChargerBooking?: boolean
    thirdPartyVehicleBookingOnly?: boolean
    thirdPartyVehicleBookingUrl?: string
  }) {
    this.localId = localId ?? generateUniqueLocalID(Fleet.usedIds, 'fleet')
    this.driverEditAccess = driverEditAccess
    this.name = name
    this.logo = logo
    this.allowVehicleBooking = allowVehicleBooking
    this.allowPrivateChargerBooking = allowPrivateChargerBooking
    this.thirdPartyVehicleBookingOnly = thirdPartyVehicleBookingOnly
    this.thirdPartyVehicleBookingUrl = thirdPartyVehicleBookingUrl

    // add id to list of used unique ids
    Fleet.usedIds.push(this.localId)
  }

  static fromDirectusData(data: DirectusCompany) {
    return new Fleet({
      driverEditAccess: data.ManualVehicleMatching ?? undefined,
      name: data.Name ?? undefined,
      logo: data.Logo ?? undefined,
      allowVehicleBooking: data.allow_vehicle_booking ?? undefined,
      allowPrivateChargerBooking: data.allow_private_charger_booking ?? undefined,
      thirdPartyVehicleBookingOnly: data.third_party_vehicle_booking_only ?? undefined,
      thirdPartyVehicleBookingUrl: data.third_party_vehicle_booking_url ?? undefined,
    })
  }

  // -------------------------------------------------------------------- //
  // ------------------------------ Getters ----------------------------- //
  // -------------------------------------------------------------------- //

  /** Returns the src path for this fleets logo pic if they have one. */
  public get logoSrc(): string | undefined {
    if (this.logo) return getDirectusImgUrl(this.logo)
  }

  // -------------------------------------------------------------------- //
  // ------------------------------ Methods ----------------------------- //
  // -------------------------------------------------------------------- //
}
